import { DateTime } from 'luxon'
import { startOfUsWeek } from 'pages/pga-coach/bookings/schedule/schedule-date-ranges'
import { daysOfWeek } from '../availability-manager/constants'

// Match schedule week grid density (bookings/schedule/calendar/calendar-utils).
export const HOUR_HEIGHT = 64
export const HOUR_HEIGHT_MOBILE = 52
export const DEFAULT_GRID_START_HOUR = 7
export const DEFAULT_GRID_END_HOUR = 19
export const MIN_GRID_HOUR = 0
export const MAX_GRID_HOUR = 24

export const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
export const dayNames = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

export const getWeekRange = referenceDate => {
  const ref = referenceDate || DateTime.local()
  // US Sun–Sat weeks (same as Schedule). Luxon startOf('week') is Monday, so
  // startOf('week').minus(1 day) wrongly maps Sunday into the previous week.
  const sunday = startOfUsWeek(ref)
  const saturday = sunday.plus({ days: 6 })
  return { start: sunday, end: saturday }
}

export const formatWeekLabel = ({ start, end }) => {
  if (start.month === end.month) {
    return `${start.toFormat('MMM d')} – ${end.toFormat('d, yyyy')}`
  }
  return `${start.toFormat('MMM d')} – ${end.toFormat('MMM d, yyyy')}`
}

const timeStringToFractionalHour = timeStr => {
  if (!timeStr) return null
  const dt = DateTime.fromISO(timeStr)
  return dt.hour + dt.minute / 60
}

export const buildRecurringByDayIndex = timeSlots => {
  const byDayIndex = daysOfWeek.reduce((acc, _d, i) => {
    acc[i] = []
    return acc
  }, {})
  ;(timeSlots || []).forEach(ts => {
    const idx = daysOfWeek.indexOf(ts.day)
    if (idx === -1) return
    const start = timeStringToFractionalHour(ts.startTime)
    const end = timeStringToFractionalHour(ts.endTime)
    if (start == null || end == null) return
    byDayIndex[idx].push({ start, end, raw: ts })
  })
  return byDayIndex
}

export const buildOverridesByDateKey = (overrides, weekStart) => {
  const byDate = {}
  ;(overrides || []).forEach(o => {
    byDate[o.date] = byDate[o.date] || []
    o.timeSlots.forEach(ts => {
      const start = timeStringToFractionalHour(ts.startTime)
      const end = timeStringToFractionalHour(ts.endTime)
      if (start == null || end == null) return
      byDate[o.date].push({ start, end, override: o })
    })
  })

  const result = Array.from({ length: 7 }, () => [])
  for (let i = 0; i < 7; i++) {
    const dateKey = weekStart.plus({ days: i }).toISODate()
    if (byDate[dateKey]) result[i] = byDate[dateKey]
  }
  return result
}

const fractionalHourFromDateTime = dt => dt.hour + dt.minute / 60

export const buildBlockedByDay = (blocks, weekStart, zone) => {
  const result = Array.from({ length: 7 }, () => [])
  ;(blocks || []).forEach(b => {
    const start = zone
      ? DateTime.fromISO(b.startTime, { zone })
      : DateTime.fromISO(b.startTime)
    const end = zone
      ? DateTime.fromISO(b.endTime, { zone })
      : DateTime.fromISO(b.endTime)
    if (!start.isValid || !end.isValid) return

    let cursor = start.startOf('day')
    const endDay = end.startOf('day')
    while (cursor <= endDay) {
      const dayDiff = Math.floor(
        cursor.diff(weekStart.startOf('day'), 'days').days,
      )
      if (dayDiff >= 0 && dayDiff < 7) {
        const segStartDt = cursor.equals(start.startOf('day')) ? start : cursor
        const segEndDt = cursor.equals(end.startOf('day'))
          ? end
          : cursor.plus({ days: 1 })

        const segStart = b.allDay ? 0 : fractionalHourFromDateTime(segStartDt)
        const segEnd = b.allDay
          ? 24
          : segEndDt.hasSame(cursor, 'day')
            ? fractionalHourFromDateTime(segEndDt)
            : 24
        if (segEnd <= segStart) {
          cursor = cursor.plus({ days: 1 })
          continue
        }
        result[dayDiff].push({
          start: segStart,
          end: segEnd,
          allDay: !!b.allDay,
          block: b,
        })
      }
      cursor = cursor.plus({ days: 1 })
    }
  })
  return result
}

export const computeVisibleHourRange = (
  recurringByDay,
  overridesByDay,
  blockedByDay,
) => {
  let minStart = DEFAULT_GRID_START_HOUR
  let maxEnd = DEFAULT_GRID_END_HOUR

  const consider = segs => {
    if (!segs) return
    segs.forEach(seg => {
      if (seg.allDay) return
      if (seg.start < minStart) minStart = seg.start
      if (seg.end > maxEnd) maxEnd = seg.end
    })
  }

  for (let i = 0; i < 7; i++) {
    consider(recurringByDay && recurringByDay[i])
    consider(overridesByDay && overridesByDay[i])
    consider(blockedByDay && blockedByDay[i])
  }

  return {
    startHour: Math.max(MIN_GRID_HOUR, Math.floor(minStart)),
    endHour: Math.min(MAX_GRID_HOUR, Math.ceil(maxEnd)),
  }
}

export const formatHourLabel = hour => {
  const period = hour >= 12 ? 'p' : 'a'
  const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  return `${display}${period}`
}

export const formatTime12 = isoOrTimeStr => {
  const dt = DateTime.fromISO(isoOrTimeStr)
  return dt.minute === 0 ? dt.toFormat('h a') : dt.toFormat('h:mm a')
}

export const formatTimeRange = (startISO, endISO) =>
  `${formatTime12(startISO)}–${formatTime12(endISO)}`

export const formatOverrideDate = isoDate =>
  DateTime.fromISO(isoDate).toFormat('EEE, MMM d, yyyy')

export const formatBlockRangeDate = (startISO, endISO) => {
  const start = DateTime.fromISO(startISO)
  const end = DateTime.fromISO(endISO)
  if (!start.isValid) return ''
  if (!end.isValid || start.hasSame(end, 'day')) {
    return start.toFormat('EEE, MMM d, yyyy')
  }
  if (start.hasSame(end, 'year')) {
    return `${start.toFormat('EEE, MMM d')} – ${end.toFormat(
      'EEE, MMM d, yyyy',
    )}`
  }
  return `${start.toFormat('EEE, MMM d, yyyy')} – ${end.toFormat(
    'EEE, MMM d, yyyy',
  )}`
}

export const formatTimeZoneAbbrev = zone => {
  if (!zone) return ''
  try {
    const dt = DateTime.now().setZone(zone)
    if (!dt.isValid) return zone
    return dt.toFormat('ZZZZ')
  } catch (e) {
    return zone
  }
}

export const formatFacilityLabel = availability => {
  const name = availability?.coachFacility?.name || ''
  const tz = formatTimeZoneAbbrev(availability?.timeZone)
  return tz ? `${name} · ${tz}` : name
}
